import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api";
import { DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { z } from "zod";

import {
  requirementIncludeSchema,
  workerRequirementIncludeSchema,
} from "../../HiringFeed/api/hiringFeed.schema";
import { jobShiftSchema, PlacementPerk } from "../types/fetchPlacements.schema";
import { type PlacementData } from "../types/placement";
import { type DiscoveryPlacementsResponse } from "./useFetchDiscoveryPlacements";

type DiscoveryPlacement = DiscoveryPlacementsResponse["data"][number];
type DiscoveryIncluded = DiscoveryPlacementsResponse["included"][number];
type DiscoveryWorkplaceInclude = Extract<DiscoveryIncluded, { type: "workplace" }>;
type DiscoveryInterviewSettingsInclude = Extract<DiscoveryIncluded, { type: "interview-settings" }>;
type DiscoveryWorkerRequirementInclude = Extract<DiscoveryIncluded, { type: "worker-requirement" }>;
type DiscoveryRequirementInclude = Extract<DiscoveryIncluded, { type: "requirement" }>;
type DiscoveryTagInclude = Extract<DiscoveryIncluded, { type: "tag" }>;

// Safely convert contract enum values to local enum types using Zod parsing.
// The contract and local types define identical string values with different
// TypeScript nominal types (e.g., contract DayOfWeek vs local DayOfTheWeek).
const localPerksSchema = z.array(z.nativeEnum(PlacementPerk));
const localAvailabilitySchema = z.array(
  z.object({
    dayOfWeek: z.nativeEnum(DayOfTheWeek),
    start: z.number(),
    end: z.number(),
  })
);

export function transformDiscoveryPlacementToData(
  placement: DiscoveryPlacement,
  included: DiscoveryIncluded[],
  credentialRequirementConfiguration: Set<string>
): PlacementData | undefined {
  const workplace = included.find(
    (item): item is DiscoveryWorkplaceInclude =>
      item.type === API_TYPES.workplace && item.id === placement.relationships.workplace.data.id
  );

  if (!workplace) {
    return undefined;
  }

  const interviewSettingsInclude = included.find(
    (item): item is DiscoveryInterviewSettingsInclude =>
      item.type === API_TYPES.interviewSettings &&
      item.relationships.workplace.data.id === workplace.id
  );

  let interviewSettings: PlacementData["interviewSettings"];
  if (interviewSettingsInclude) {
    const availabilityResult = localAvailabilitySchema.safeParse(
      interviewSettingsInclude.attributes.availability
    );
    if (!availabilityResult.success) {
      return undefined;
    }
    interviewSettings = {
      scheduleInterviews: interviewSettingsInclude.attributes.scheduleInterviews,
      availability: availabilityResult.data,
      id: interviewSettingsInclude.id ?? workplace.id,
    };
  }

  // Extract worker requirements using the same pattern as transformResponseIntoHiringFeed
  const allWorkerRequirements =
    placement.relationships.workerRequirements?.data
      .map((workerRequirementRef) => {
        const workerRequirement = included.find(
          (item): item is DiscoveryWorkerRequirementInclude =>
            item.type === API_TYPES.workerRequirement &&
            item.id === workerRequirementRef.id &&
            item.attributes.qualifications.some((qualification) =>
              placement.attributes.workerTypes?.includes(qualification)
            )
        );

        if (!workerRequirement) {
          return undefined;
        }

        const requirement = included.find(
          (item): item is DiscoveryRequirementInclude =>
            item.type === API_TYPES.requirement &&
            item.id === workerRequirement.relationships.requirement.data.id
        );

        if (!requirement) {
          return undefined;
        }

        // Parse through local schemas to convert contract enum types to local types.
        // Skip the requirement (rather than crashing the list) if an enum is unrecognized.
        const parsedWorkerRequirementResult =
          workerRequirementIncludeSchema.shape.attributes.safeParse(workerRequirement.attributes);
        const parsedRequirementResult = requirementIncludeSchema.shape.attributes.safeParse({
          ...requirement.attributes,
          name: requirement.attributes.name ?? "",
        });

        if (!parsedWorkerRequirementResult.success || !parsedRequirementResult.success) {
          return undefined;
        }

        return {
          ...parsedWorkerRequirementResult.data,
          id: workerRequirement.id,
          requirement: {
            ...parsedRequirementResult.data,
            id: requirement.id,
          },
        };
      })
      .filter((request): request is NonNullable<typeof request> => isDefined(request)) ?? [];

  const { isExemptFromDocumentRequirements } = placement.meta;

  const workerRequirements = isExemptFromDocumentRequirements
    ? undefined
    : allWorkerRequirements.filter(
        (workerRequirement) =>
          !credentialRequirementConfiguration.has(workerRequirement.requirement.id)
      );

  const credentialRequirements = allWorkerRequirements.filter((workerRequirement) =>
    credentialRequirementConfiguration.has(workerRequirement.requirement.id)
  );

  const tags = placement.relationships.tags?.data
    .map((tagRef) => {
      const tagData = included.find(
        (item): item is DiscoveryTagInclude => item.type === API_TYPES.tag && item.id === tagRef.id
      );
      return tagData;
    })
    .filter((tag): tag is DiscoveryTagInclude => isDefined(tag));

  // Parse jobShifts and perks through local schemas to convert contract enum types.
  // Use safeParse so a single malformed placement (e.g. unrecognized enum) is skipped
  // rather than crashing the list render.
  const jobShiftsResult = z.array(jobShiftSchema).safeParse(placement.attributes.jobShifts);
  const perksResult = localPerksSchema.safeParse(placement.attributes.perks);
  if (!jobShiftsResult.success || !perksResult.success) {
    return undefined;
  }
  const jobShifts = jobShiftsResult.data;
  const perks = perksResult.data;

  return {
    id: placement.id,
    status: placement.attributes.status,
    title: placement.attributes.title,
    workerTypes: placement.attributes.workerTypes,
    jobShifts,
    payRange: placement.attributes.payRange,
    perks,
    distance: placement.attributes.distanceInMiles,
    isLicenseEligible: placement.meta.isLicenseEligible,
    interviewSettings,
    interviews: [],
    applications: undefined,
    workerRequirements:
      workerRequirements && workerRequirements.length > 0 ? workerRequirements : undefined,
    credentialRequirements,
    tags,
    workplace: {
      id: workplace.id,
      name: workplace.attributes.name,
      neighborhood: workplace.attributes.address?.neighborhood,
      rating: workplace.attributes.rating ?? undefined,
      photos: workplace.attributes.photos,
      isProspect: workplace.attributes.placementSettings?.isProspect,
      interviewDays: workplace.meta?.interviewDays,
      interviewSocialProof: workplace.meta?.interviewSocialProof,
    },
    webApplicationLink: placement.attributes.webApplicationLink,
    allowRequestInterview: placement.meta.allowRequestInterview ?? false,
    score: placement.meta.score,
    scoreBreakdown: placement.meta.scoreBreakdown,
    baseRate: placement.meta.baseRate,
    totalRate: placement.meta.totalRate,
    totalRateBreakdown: placement.meta.totalRateBreakdown,
    matchedType: placement.meta.matchedType,
    applicationMethod:
      placement.meta.applicationMethod === "UNRECOGNIZED_"
        ? undefined
        : placement.meta.applicationMethod,
    transparencyPayRates: placement.attributes.transparencyPayRates,
    isRecruiterScreeningEnabled: placement.meta.isRecruiterScreeningEnabled,
    isEasyScheduling: placement.meta.isEasyScheduling,
    interviewInstructions: placement.attributes.interviewInstructions,
    isFavorite: placement.meta.isFavorited ?? false,
    workerFavoritePlacementId: placement.relationships.workerFavoritePlacement?.data?.id,
  };
}
